import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import authService, { User } from '../services/authService';

interface AuthContextType {
  user: User | null;
  client: any | null; // Added client to AuthContextType
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  register: (userData: { email: string; username: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const isAuthenticated = !!user && authService.isAuthenticated();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const hasToken = authService.isAuthenticated();
        
        if (hasToken) {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        // Clear invalid tokens
        await authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authService.login({ email, password });
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('AuthContext: Login failed in AuthContext:', error);
      throw error; // Re-throw the error so LoginForm can catch it
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: { email: string; username: string; password: string }) => {
    setIsLoading(true);
    try {
      // Register the user - no auto-login or auto-verification
      const response = await authService.register(userData);
      
      // Show success message but don't log in
      // User needs to verify email before they can log in
      console.log('Registration successful:', response);
      
      // Don't auto-login - user must verify email first
      // Don't auto-verify - user must click email verification link
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithTokens = async (accessToken: string, refreshToken: string) => {
    setIsLoading(true);
    try {
      console.log('AuthContext: Storing tokens...', { 
        hasAccessToken: !!accessToken, 
        hasRefreshToken: !!refreshToken 
      });
      
      // Store tokens
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      
      // Verify tokens were stored
      const storedAccessToken = localStorage.getItem('access_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');
      console.log('AuthContext: Tokens stored verification:', { 
        storedAccessToken: !!storedAccessToken, 
        storedRefreshToken: !!storedRefreshToken 
      });
      
      // Small delay to ensure tokens are stored
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('AuthContext: Getting current user...');
      // Get current user info
      const currentUser = await authService.getCurrentUser();
      console.log('AuthContext: User retrieved:', currentUser);
      setUser(currentUser);
    } catch (error) {
      console.error('AuthContext: Token login failed:', error);
      // Clear tokens on failure
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
      // Clear all cached data to prevent cross-user data leaks
      queryClient.clear();
      // Reset theme to light mode on logout for consistent landing page experience
      localStorage.setItem('theme', 'light');
      // Force update the document class immediately
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add('light');
      console.log('Logout: Reset theme to light mode');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    if (authService.isAuthenticated()) {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Failed to refresh user:', error);
        await logout();
      }
    }
  };

  const value: AuthContextType = {
    user,
    client: user?.client || null, // Provide client from user object
    isLoading,
    isAuthenticated,
    login,
    loginWithTokens,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};